/**
 * Template editor schemas and types.
 * Consolidates media types, format schemas, bindings, elements, and template structure.
 */
import { z } from 'zod';

export const CURRENT_TEMPLATE_SCHEMA_VERSION = 1;

/** Supported media types */
export type MediaType = 'text' | 'image' | 'date' | 'color' | 'int' | 'float';

/** Format options per media type */
export type TextFormat  = { kind?: 'uppercase'|'lowercase'|'title' };
export type DateFormat  = { pattern: string };
export type NumberFormat = { decimals?: number; thousands?: boolean; pad?: number };
export type ColorFormat = Record<string, never>;
export type ImageFormat = { fit?: 'cover'|'contain'|'fill'|'none'; focalPoint?: { x:number; y:number } };

/** Format mapping by media type */
export type FormatByMedia = {
  text:  TextFormat;
  image: ImageFormat;
  date:  DateFormat;
  color: ColorFormat;
  int:   NumberFormat;
  float: NumberFormat;
};

/** Format schemas */
export const zTextFormat   = z.object({ kind: z.enum(['uppercase','lowercase','title']).optional() });
export const zDateFormat   = z.object({ pattern: z.string().min(1) });
export const zNumberFormat = z.object({
  decimals: z.number().int().min(0).max(10).optional(),
  thousands: z.boolean().optional(),
  pad: z.number().int().min(0).max(10).optional(),
});
export const zColorFormat  = z.object({}).strict();
export const zImageFormat  = z.object({
  fit: z.enum(['cover','contain','fill','none']).optional(),
  focalPoint: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }).optional(),
});

/** Data binding references */
export type BindingRef =
  | { kind: 'slot'; slotId: string; attributePath: string }
  | { kind: 'selector'; selectorId: string }
  | { kind: 'override'; overrideKey: string };

export const zBindingRef = z.union([
  z.object({
    kind: z.literal('slot'),
    slotId: z.string().min(1),
    attributePath: z.string().min(1),
  }),
  z.object({
    kind: z.literal('selector'),
    selectorId: z.string().min(1),
  }),
  z.object({
    kind: z.literal('override'),
    overrideKey: z.string().min(1),
  }),
]);

/** Container for static or dynamic values */
export type StaticOrDynamic<T, M extends MediaType = MediaType> =
  | { mode: 'static'; mediaType: M; staticValue: T; format?: FormatByMedia[M]; }
  | {
      mode: 'dynamic'; mediaType: M; format?: FormatByMedia[M];
      dynamic: {
        source: BindingRef;
        defaultValue?: T;
        label?: string;
        description?: string;
      }
    };

/** Zod schema factory for StaticOrDynamic values */
export function zStaticOrDynamic<T, M extends MediaType>(
  media: M,
  staticSchema: z.ZodType<T>,
  formatSchema?: z.ZodType<any>
) {
  return z.union([
    z.object({
      mode: z.literal('static'),
      mediaType: z.literal(media),
      staticValue: staticSchema,
      format: (formatSchema ?? z.any()).optional(),
    }),
    z.object({
      mode: z.literal('dynamic'),
      mediaType: z.literal(media),
      format: (formatSchema ?? z.any()).optional(),
      dynamic: z.object({
        source: zBindingRef,
        defaultValue: staticSchema.optional(),
        label: z.string().optional(),
        description: z.string().optional(),
      }),
    }),
  ]);
}

/** Hex color validation (#RRGGBB) */
const zColorHex = z.string().regex(/^#[0-9a-fA-F]{6}$/, 'Must be a valid hex color code');

/** Supported aspect ratios */
/** ========================================================================== */
/** Enums                                                                     */
/** ========================================================================== */
export const zAspectRatio = z.enum(['1:1', '16:9', '9:16', '4:5']);
export type AspectRatio = z.infer<typeof zAspectRatio>;

const zEntityType = z.enum(['Player', 'Team', 'Sponsor', 'Match', 'Club', 'Venue']);
export type EntityType = z.infer<typeof zEntityType>;

const zHorizontalAlign = z.enum(['left', 'center', 'right']);
const zVerticalAlign = z.enum(['top', 'center', 'bottom']);

const zFontWeight = z.enum(['100', '200', '300', '400', '500', '600', '700', '800', '900']);
const zFontStyle = z.enum(['normal', 'italic']);

// Font variant that combines weight and style (e.g., "regular", "italic", "700", "700italic")
const zFontVariant = z.union([
  z.enum(['regular', 'italic']), // Google Fonts standard variants
  z.enum(['100', '200', '300', '400', '500', '600', '700', '800', '900']), // Numeric weights
  z.string().regex(/^[1-9]00italic$/, 'Must be a valid weight+italic combination') // e.g., "700italic"
]);
const zEllipsis = z.enum(['…', '...']);

const zGradientType = z.enum(['linear', 'radial']);
const zLineCap = z.enum(['butt', 'round', 'square']);
const zLineJoin = z.enum(['miter', 'round', 'bevel']);

/** Reusable entity slot */
const zSlot = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  entityType: zEntityType,
  description: z.string().optional(),
});
export type Slot = z.infer<typeof zSlot>;

/** Alignment options */
const zAlign = z.object({
  h: zHorizontalAlign.default('left'),
  v: zVerticalAlign.default('top'),
});
export type Align = z.infer<typeof zAlign>;

/** Static or dynamic text content (text, date, int, float) */
const zTextContentValue = z.union([
  zStaticOrDynamic('text', z.string(), zTextFormat),
  zStaticOrDynamic('date', z.string(), zDateFormat),
  zStaticOrDynamic('int', z.number().int(), zNumberFormat),
  zStaticOrDynamic('float', z.number(), zNumberFormat),
]);

/** Text values type-safe union */
const zTextValue = z.union([
  zStaticOrDynamic('text', z.string(), zTextFormat),
  zStaticOrDynamic('date', z.string(), zDateFormat),
  zStaticOrDynamic('int', z.number().int(), zNumberFormat),
  zStaticOrDynamic('float', z.number(), zNumberFormat),
]);

/** Image source value */
export const zImageStaticSource = z.object({
  mediaId: z.string().regex(/^[0-9a-fA-F]{24}$/, 'Must be a valid ObjectId').optional().nullable(),
  path: z.string().optional().nullable(),
  relativePath: z.string().optional().nullable(),
  origin: z.enum(['STORE', 'CUSTOMER']).optional(),
});
const zImageValue = zStaticOrDynamic('image', zImageStaticSource, zImageFormat);

/** Color value (validated hex) */
const zColorValue = zStaticOrDynamic('color', zColorHex, zColorFormat);

export type TextContentValue = z.infer<typeof zTextContentValue>;
export type TextValue = z.infer<typeof zTextValue>;
export type ImageValue = z.infer<typeof zImageValue>;
export type ColorValue = z.infer<typeof zColorValue>;

/** Solid fill style */
const zSolidFill = z.object({
  kind: z.literal('solid'),
  color: zColorValue,
  opacity: z.number().min(0).max(1).optional(),
});

/** Gradient stop */
const zGradientStop = z.object({
  offset: z.number().min(0).max(1),
  color: zColorValue,
  opacity: z.number().min(0).max(1).optional(),
});

/** Gradient fill style */
const zGradientFill = z.object({
  kind: z.literal('gradient'),
  gradientType: zGradientType,
  stops: z.array(zGradientStop).min(2, 'Gradient requires at least 2 stops'),
  angle: z.number().min(0).max(360).optional(),
});

/** Fill union */
const zFill = z.discriminatedUnion('kind', [zSolidFill, zGradientFill]);
export type Fill = z.infer<typeof zFill>;

/** Stroke style */
const zStroke = z.object({
  color: zColorValue,
  width: z.number().min(0).default(1),
  dash: z.array(z.number()).optional(),
});
export type Stroke = z.infer<typeof zStroke>;

/** Effects Schemas */
export const zShadowEffect = z.object({
  id: z.string(),
  type: z.literal('drop-shadow'),
  x: z.number().default(0),
  y: z.number().default(4),
  blur: z.number().default(4),
  color: zColorValue,
  opacity: z.number().min(0).max(1).default(0.5),
});

export const zBlurEffect = z.object({
  id: z.string(),
  type: z.literal('layer-blur'),
  radius: z.number().default(4),
});

export const zEffect = z.discriminatedUnion('type', [zShadowEffect, zBlurEffect]);

/** Base element properties */
const zBaseElement = z.object({
  id: z.string().min(1),
  type: z.string(),
  name: z.string().optional(),
  x: z.number().default(0),
  y: z.number().default(0),
  width: z.number(),
  height: z.number(),
  rotation: z.number().default(0),
  zIndex: z.number().int(),
  visible: z.boolean().default(true).optional(),
  locked: z.boolean().default(false).optional(),
  isWatermark: z.boolean().default(false).optional(),
  opacity: z.number().min(0).max(1).default(1),
  center: z.object({
    horizontal: z.boolean().optional(),
    vertical: z.boolean().optional(),
  }).optional(),
  effects: z
    .array(zEffect)
    .optional()
    .default([])
    .refine(
      (effects) => {
        const types = new Set();
        for (const e of effects) {
          if (types.has(e.type)) return false;
          types.add(e.type);
        }
        return true;
      },
      { message: 'Only one effect of each type is allowed' },
    ),
});

/** Rectangle element */
const zRectElement = zBaseElement.extend({
  type: z.literal('rect'),
  width: z.number().min(0),
  height: z.number().min(0),
  fill: zFill.optional(),
  stroke: zStroke.optional(),
  cornerRadius: z.number().min(0).optional(),
});

/** Circle/Ellipse element */
const zCircleElement = zBaseElement.extend({
  type: z.literal('circle'),
  width: z.number().min(0),
  height: z.number().min(0),
  fill: zFill.optional(),
  stroke: zStroke.optional(),
});

/** Font sizing options */
const zFontSizing = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('manual'),
    fontSize: z.number().positive(),
    ellipsis: zEllipsis.optional(),
  }),
  z.object({
    mode: z.literal('fit'),
    // minFontSize: z.number().positive().optional(), // Future use
    // maxFontSize: z.number().positive().optional(), // Future use
  }),
]);
export type FontSizing = z.infer<typeof zFontSizing>;

/** Text element */
const zTextElement = zBaseElement.extend({
  type: z.literal('text'),
  width: z.number().min(0),
  height: z.number().min(0),
  content: zTextContentValue,
  fontFamily: z.string(),
  fontSizing: zFontSizing.default({ mode: 'manual', fontSize: 32 }),
  fontWeight: zFontWeight.default('400'),
  fontStyle: zFontStyle.default('normal'),
  fontVariant: zFontVariant.optional(),
  lineHeight: z.number().positive().optional(),
  letterSpacing: z.number().optional(),
  color: zColorValue,
  align: zAlign,
  maxLines: z.number().int().positive().optional(),
});

/** Image element */
const zImageElement = zBaseElement.extend({
  type: z.literal('image'),
  width: z.number().min(0),
  height: z.number().min(0),
  source: zImageValue,
  focalPoint: z.object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) }).optional(),
  filters: z.object({
    invert: z.boolean().optional(),
    grayscale: z.boolean().optional(),
    brightness: z.number().min(0).max(2).optional(),
    contrast: z.number().min(0).max(2).optional(),
    saturate: z.number().min(0).max(2).optional(),
  }).optional(),
  clipRadius: z.number().min(0),
});

/** SVG element */
const zSvgElement = zBaseElement.extend({
  type: z.literal('svg'),
  width: z.number().min(0),
  height: z.number().min(0),
  source: zImageValue,
  colorMapping: z.record(z.string(), zColorValue).optional(),
  fillMode: z.enum(['original', 'custom', 'monochrome']).default('original'),
  monochromeColor: zColorValue.optional(),
});

/** Line element */
const zLineElement = zBaseElement.extend({
  type: z.literal('line'),
  points: z.array(z.number()).min(4, 'Line requires at least 2 points (x1, y1, x2, y2)'),
  stroke: zStroke.optional(),
  lineCap: zLineCap.optional(),
  lineJoin: zLineJoin.optional(),
});

/** Element union */
export const zElement = z.lazy(() => z.discriminatedUnion('type', [
  zRectElement,
  zCircleElement,
  zTextElement,
  zImageElement,
  zSvgElement,
  zLineElement,
]));
export type Element = z.infer<typeof zElement>;

export type RectElement = z.infer<typeof zRectElement>;
export type CircleElement = z.infer<typeof zCircleElement>;
export type TextElement = z.infer<typeof zTextElement>;
export type ImageElement = z.infer<typeof zImageElement>;
export type SvgElement = z.infer<typeof zSvgElement>;
export type LineElement = z.infer<typeof zLineElement>;

/** Complete template schema */
export const zTemplate = z.object({
  schemaVersion: z.number().int().positive(),
  id: z.string().regex(/^[0-9a-fA-F]{24}$/, 'Must be a valid ObjectId'),
  title: z.string(),
  aspectRatio: zAspectRatio,
  canvas: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  }),
  elements: z.array(zElement).default([]),
  slots: z.array(zSlot).default([]),
  meta: z.object({
    createdBy: z.string().optional(),
    updatedBy: z.string().optional(),
    createdAt: z.string().datetime().optional(),
    updatedAt: z.string().datetime().optional(),
    thumbnailPath: z.string().optional(),
    categories: z.array(z.string()).optional(),
    isPublished: z.boolean().optional(),
    version: z.number().int().positive().optional(),
    versionIncrementedAt: z.string().datetime().nullable().optional(),
    importedFromTemplateId: z.string().regex(/^[0-9a-fA-F]{24}$/, 'Must be a valid ObjectId').nullable().optional(),
    importedFromTemplateVersion: z.number().int().positive().nullable().optional(),
  }).optional(),
  department: z.string().nullable().default('soccer'),
  usageStats: z
    .object({
      importCount: z.number().default(0),
      postCount: z.number().default(0),
    })
    .optional(),
});
export type Template = z.infer<typeof zTemplate>;

// Re-export element schemas
export {
  zRectElement,
  zCircleElement,
  zTextElement,
  zImageElement,
  zSvgElement,
  zLineElement,
};
